import { useAuth } from "@/context/AuthContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LivePill } from "@/components/app/Badges";
import BrandLogo from "@/components/app/BrandLogo";
import { LogOut, User, Network } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

async function openNms() {
  try {
    const { data } = await api.get("/nms");
    if (!data?.url) { toast.error("NMS URL not configured"); return; }
    const line = `Username: ${data.username}\nPassword: ${data.password}`;
    try { await navigator.clipboard?.writeText(line); } catch { /* clipboard may fail on http contexts */ }
    toast.success(`NMS opened — credentials copied`, {
      description: `${data.username} / ${data.password} · paste when the login prompt appears`,
      duration: 8000,
    });
    window.open(data.url, "_blank", "noopener,noreferrer");
  } catch (e) {
    toast.error("Could not fetch NMS info");
  }
}

export default function AppShell({ children, title, subtitle, nav = [] }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#05070d] text-slate-100">
      <header className="sticky top-0 z-40 border-b border-slate-800/80 bg-slate-950/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/" data-testid="app-logo" className="flex items-center gap-3">
            <BrandLogo height={40} />
            <div className="hidden sm:block leading-tight border-l border-slate-800 pl-3">
              <div className="font-display font-extrabold text-base tracking-tight text-purple-300">GOA</div>
              <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Complain Center</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {nav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.testid || n.label.toLowerCase().replace(/\s+/g,"-")}`}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${location.pathname === n.to ? "bg-slate-800 text-white" : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60"}`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <LivePill />
            <Button
              data-testid="open-nms-btn"
              onClick={openNms}
              size="sm"
              variant="outline"
              className="border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-200"
              title="Open NMS (Network Management System) in a new tab"
            >
              <Network className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Open NMS</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="user-menu-btn" variant="outline" size="sm" className="border-slate-700 bg-slate-900 hover:bg-slate-800">
                  <User className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">{user?.name || "Account"}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-slate-900 border-slate-700 text-slate-100" align="end">
                <DropdownMenuLabel className="text-slate-400">
                  {user?.email}
                  <div className="text-[10px] font-mono-alt uppercase tracking-wider text-purple-400 mt-0.5">{user?.role}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-slate-800" />
                <DropdownMenuItem data-testid="logout-btn" onClick={() => { logout(); navigate("/login"); }} className="cursor-pointer">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {(title || nav.length) && (
          <div className="md:hidden max-w-7xl mx-auto px-4 pb-3 flex gap-1 overflow-x-auto">
            {nav.map((n) => (
              <Link key={n.to} to={n.to} className={`px-3 py-1.5 rounded-md text-xs whitespace-nowrap ${location.pathname === n.to ? "bg-slate-800 text-white" : "text-slate-400 bg-slate-900/60"}`}>{n.label}</Link>
            ))}
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        {(title || subtitle) && (
          <div className="mb-6 lg:mb-8">
            {title && <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white">{title}</h1>}
            {subtitle && <p className="text-slate-400 mt-1.5 text-sm sm:text-base">{subtitle}</p>}
          </div>
        )}
        {children}
      </main>
    </div>
  );
}
